<template>
    <div class="alarms-panel">
        <!-- TYPE FILTER (local to the alarms section) -->
        <div class="d-flex align-items-center justify-content-between mb-2">
            <div class="fw-semibold">Filtrer par type</div>
            <button class="btn btn-sm btn-outline-secondary" @click="toggleTypes">
                {{ showTypes ? 'Masquer' : 'Afficher' }}
            </button>
        </div>

        <div v-if="showTypes" class="types-card mb-3">
            <div class="types-toolbar">
                <div class="text-muted small">
                    {{ selectedAlarmFilterKeys.length ? selectedAlarmFilterKeys.length + ' sélectionné(s)' : 'Tous les types' }}
                </div>
                <div class="d-flex gap-2">
                    <button class="btn btn-sm btn-light" @click="selectAllTypes" :disabled="!alarmFilters.length">
                        Tout
                    </button>
                    <button class="btn btn-sm btn-light" @click="clearTypes">
                        Aucun
                    </button>
                </div>
            </div>

            <div class="types-list">
                <label v-for="filter in alarmFilters" :key="filter.key" class="type-item">
                    <input class="form-check-input me-2" type="checkbox" :value="filter.key"
                        v-model="selectedAlarmFilterKeys" />
                    <span class="type-fallback">
                        {{ filter.label }}
                    </span>
                </label>
            </div>
        </div>

        <!-- LIST HEADER -->
        <div class="d-flex align-items-center justify-content-between mb-2">
            <div class="text-muted small">
                <span v-if="loading">Chargement…</span>
                <span v-else>
                    {{ alarms.length }} alarme(s) affichée(s)
                </span>
            </div>

            <div class="d-flex gap-2">
                <button class="btn btn-sm btn-outline-secondary" @click="refresh" :disabled="loading">
                    Rafraîchir
                </button>
            </div>
        </div>

        <!-- EMPTY STATE -->
        <div v-if="!loading && (!alarms || alarms.length === 0)" class="empty">
            Aucune alarme ne correspond aux filtres.
        </div>

        <!-- LIST -->
        <div v-else class="alarms-list small">
            <div v-for="a in alarms" :key="a.id" class="alarm-row">
                <div class="alarm-row-grid">
                    <!-- LEFT: bloc alarme inchangé -->
                    <div class="alarm-meta">
                        <div class="alarm-date">
                            {{ a.date }}
                        </div>
                        <div class="alarm-speed">
                            {{ formatSpeed(a.speed ?? a.vitesse ?? a.kmh ?? null) }}
                        </div>
                    </div>

                    <div class="alarm-title">
                        <AlarmName :alarm="a" :balise="a.balise || null" />
                    </div>

                    <div class="alarm-vehicle">
                        <VehicleNameDisplay :balise="a.balise || null" :compact=true />
                    </div>

                    <div class="alarm-location">
                        <div class="alarm-address" :title="a.address || a.adress || a.location || ''">
                            {{ a.address || a.adress || a.location || 'Adresse indisponible' }}
                        </div>

                        <div v-if="a.data" class="alarm-data" :title="a.data">
                            {{ a.data }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import AlarmRepository from "@/repositories/alarm.repo.js";
import AlarmName from "@/components/display/AlarmDisplay.vue";
import VehicleNameDisplay from "@/components/display/VehicleNameDisplay.vue";
import { readUserPageFilters, writeUserPageFilters } from "@/helpers/localUserFilterStorage.js";

const PAGE_FILTER_KEY = "dashboard-alarms-panel";

export default {
    name: "AlarmsPanel",
    components: {
        AlarmName,
        VehicleNameDisplay
    },
    props: {
        clientId: { type: Number, default: null },
        baliseId: { type: Number, default: null },
        startDate: { type: String, default: null },
        endDate: { type: String, default: null },
    },
    data() {
        return {
            showTypes: true,
            alarmFilters: [
                {
                    key: "corridoring",
                    label: "Corridoring",
                    alarmTypeIds: [39],
                },
                {
                    key: "geofencing",
                    label: "Geofencing",
                    alarmTypeIds: [42],
                },
                {
                    key: "stationnement",
                    label: "Stationnement",
                    alarmTypeIds: [47],
                },
                {
                    key: "sos",
                    label: "SOS / Verticalité",
                    alarmTypeIds: [40, 49],
                },
            ],
            selectedAlarmFilterKeys: [],
            loading: false,
            alarms: [],
            total: 0,
            refreshAbortController: null,
        };
    },
    computed: {
        selectedAlarmTypeIds() {
            return [
                ...new Set(
                    this.alarmFilters
                        .filter(filter => this.selectedAlarmFilterKeys.includes(filter.key))
                        .flatMap(filter => filter.alarmTypeIds)
                )
            ];
        },
    },
    watch: {
        clientId() { this.resetAndRefresh(); },
        baliseId() { this.resetAndRefresh(); },
        startDate() { this.resetAndRefresh(); },
        endDate() { this.resetAndRefresh(); },
        selectedAlarmFilterKeys() {
            this.persistFilters();
            this.resetAndRefresh();
        },
    },
    mounted() {
        this.restoreFilters();
    },
    beforeUnmount() {
        if (this.refreshAbortController) {
            this.refreshAbortController.abort();
        }
    },
    methods: {
        restoreFilters() {
            const saved = readUserPageFilters(null, PAGE_FILTER_KEY);
            const savedKeys = Array.isArray(saved.selectedAlarmFilterKeys)
                ? saved.selectedAlarmFilterKeys.map(v => String(v))
                : [];

            const allowedKeys = this.alarmFilters.map(filter => filter.key);
            const validSavedKeys = savedKeys.filter(key => allowedKeys.includes(key));

            this.selectedAlarmFilterKeys = validSavedKeys.length
                ? validSavedKeys
                : this.alarmFilters.map(filter => filter.key);
        },
        persistFilters() {
            writeUserPageFilters(null, PAGE_FILTER_KEY, {
                selectedAlarmFilterKeys: this.selectedAlarmFilterKeys
            });
        },
        toggleTypes() {
            this.showTypes = !this.showTypes;
        },
        selectAllTypes() {
            this.selectedAlarmFilterKeys = this.alarmFilters.map(filter => filter.key);
        },
        clearTypes() {
            this.selectedAlarmFilterKeys = [];
        },
        resetAndRefresh() {
            this.refresh();
        },
        async refresh() {
            if (this.refreshAbortController) {
                this.refreshAbortController.abort();
            }

            if (!this.selectedAlarmTypeIds.length) {
                this.total = 0;
                this.alarms = [];
                this.loading = false;
                this.refreshAbortController = null;
                return;
            }

            const controller = new AbortController();
            this.refreshAbortController = controller;
            this.loading = true;

            try {
                const result = await AlarmRepository.search({
                    clientId: this.clientId,
                    baliseId: this.baliseId,
                    alarmTypeIds: this.selectedAlarmTypeIds,
                    startDate: this.startDate,
                    endDate: this.endDate,
                    signal: controller.signal,
                });

                this.total = result?.count ?? 0;
                this.alarms = result?.rows ?? [];
            } catch (e) {
                if (e?.name === "AbortError" || e?.code === "ERR_CANCELED") {
                    return;
                }
                this.total = 0;
                this.alarms = [];
            } finally {
                if (this.refreshAbortController === controller) {
                    this.loading = false;
                    this.refreshAbortController = null;
                }
            }
        },
        getClientName(alarm) {
            const name =
                alarm?.balise?.client?.name ||
                alarm?.balise?.clientName ||
                alarm?.client?.name ||
                alarm?.clientName ||
                null;
            return name ? name : "—";
        },
        formatSpeed(v) {
            if (v === null || v === undefined || v === "") return "— km/h";
            const n = Number(v);
            if (Number.isNaN(n)) return `${v} km/h`;
            return `${Math.round(n)} km/h`;
        },
    },
};
</script>

<style scoped>
.alarms-panel {
    padding: 2px 0 0;
}

.types-card {
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-radius: 14px;
    background: rgba(15, 23, 42, 0.02);
    overflow: hidden;
}

.types-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    border-bottom: 1px solid rgba(15, 23, 42, 0.06);
    background: rgba(255, 255, 255, 0.7);
}

.types-list {
    max-height: 180px;
    overflow: auto;
    padding: 8px 10px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px 10px;
}

.type-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border-radius: 12px;
    cursor: pointer;
    user-select: none;
    border: 1px solid transparent;
}

.type-item:hover {
    background: rgba(255, 255, 255, 0.85);
    border-color: rgba(15, 23, 42, 0.06);
}

.type-icon,
.alarm-icon {
    width: 28px;
    height: 28px;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border: 1px solid rgba(15, 23, 42, 0.08);
    background: #fff;
}

.type-icon img,
.alarm-icon img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.type-fallback {
    font-size: 12px;
    font-weight: 700;
    color: rgba(15, 23, 42, 0.75);
}

.type-name {
    font-size: 13px;
    color: rgba(15, 23, 42, 0.9);
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.empty {
    padding: 18px 12px;
    border-radius: 14px;
    border: 1px dashed rgba(15, 23, 42, 0.18);
    color: rgba(15, 23, 42, 0.55);
    text-align: center;
}

.alarms-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.alarm-row {
    display: block;
    padding: 8px 10px;
    border-radius: 14px;
    border: 1px solid rgba(15, 23, 42, 0.08);
    background: #fff;
}

.alarm-row-grid {
    display: grid;
    grid-template-columns: 120px minmax(170px, 220px) minmax(170px, 220px) minmax(0, 1fr);
    gap: 14px;
    align-items: center;
}

.alarm-meta {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 2px;
    white-space: nowrap;
}

.alarm-vehicle {
    min-width: 0;
    overflow: hidden;
}

.alarm-title {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
    overflow: hidden;
    text-align: center;
}

.alarm-date {
    font-size: 12px;
    color: rgba(15, 23, 42, 0.55);
    white-space: nowrap;
    line-height: 1.1;
}

.alarm-location {
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 3px;
}

.alarm-address {
    font-size: 13px;
    color: rgba(15, 23, 42, 0.75);
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    line-clamp: 2;
    text-align: left;
    line-height: 1.2;
    word-break: break-word;
}

.alarm-data {
    font-size: 12px;
    color: rgba(15, 23, 42, 0.55);
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    line-clamp: 2;
    text-align: left;
    line-height: 1.2;
    word-break: break-word;
}

.alarm-speed {
    font-size: 11px;
    font-weight: 800;
    color: rgba(15, 23, 42, 0.7);
    background: rgba(15, 23, 42, 0.04);
    border: 1px solid rgba(15, 23, 42, 0.06);
    padding: 2px 8px;
    border-radius: 999px;
    white-space: nowrap;
    line-height: 1.1;
    width: fit-content;
}

@media (max-width: 520px) {
    .types-list {
        grid-template-columns: 1fr;
    }

    .alarm-row-grid {
        grid-template-columns: 105px minmax(150px, 200px) minmax(150px, 200px) minmax(0, 1fr);
        gap: 10px;
    }

    .alarm-meta,
    .alarm-title,
    .alarm-vehicle,
    .alarm-location,
    .alarm-address,
    .alarm-data {
        max-width: 100%;
    }
}
</style>
